import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormGroup, AbstractControl, Validators, UntypedFormBuilder, ValidationErrors } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as _ from 'lodash';

import { PlaylistsService } from '../../core/services/http-services/playlists.service';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { AuthService } from '../../core/services/auth/auth.service';
import { UIService } from '../../core/services/utils/ui.service';
import { PlaylistItemsService } from '../../core/services/http-services/playlistItems.service';

import { Machine } from '../../core/models/entities/machine.type';
import { LibraryEditItemTypeEnum } from './../../core/enums/library-edit-item-type.enum';
import { PersonalDataViewModel } from '../../core/models/view-models/account-models/user-models/personal-data-view-model';
import { LibraryEditItem } from '../../core/models/library-edit-item.model';
import { TagService } from '../../core/services/http-services/tag.service';
import { TagViewModel } from '../../core/models/view-models/tag-models/tag-view.model';
import { UpdateProfileViewModel } from '../../core/models/view-models/account-models/user-models/update-profile-view-model.type';
import { AppConstants } from '../../app.constants';
import { ModalConfirmChangePlaylistTypeComponent } from '../playlists/modal-change-playlist-type-confirm/modal-confirm-change-playlist-type/modal-confirm-change-playlist-type.component';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { TransitionEffect, TransitionEffectLabels } from '../../core/constants/transition-effect.const';

@Component({
  selector: 'app-main-library-form',
  templateUrl: './main-library-form.component.html',
  styleUrls: ['./main-library-form.component.scss'],
})
export class MainLibraryFormComponent implements OnInit, OnDestroy {
  @Input() playlist: PlaylistDTO
  @Input() editItem: Partial<LibraryEditItem>;
  @Input() editItemType: LibraryEditItemTypeEnum;
  @Input() existedTitles: string[] = [];

  @Output() exit: EventEmitter<void> = new EventEmitter();
  @Output() locationChanged: EventEmitter<string> = new EventEmitter();

  @ViewChild('playlistTitleElement', { static: true }) playlistTitleElement: ElementRef;

  public locations: Machine[];
  public isBottomPanelVisible: boolean;
  public autocompleteUserInput = '';
  public selectedUsersShared: PersonalDataViewModel[] = [];
  public filteredUsers: PersonalDataViewModel[];
  public dateCreated: Date;
  public isOwner = true;
  public form: UntypedFormGroup;
  public UsersWhoHaveAccessIds: string[] = [];
  public libraryEditItemTypes = LibraryEditItemTypeEnum;
  public isTitleFocused: boolean = false;
  public tooltipMessage: string = AppConstants.MAWI_REPEAT_BUTTON_TOOLTIP_MESSAGE;
  public transitionEffects = TransitionEffect;
  public transitionEffectsLabels = TransitionEffectLabels;

  private currentUserId: string;
  private allUsers: PersonalDataViewModel[] = [];
  private subscriptions: Subscription = new Subscription();

  get Title(): AbstractControl {
    return this.form.get('Title');
  }

  get IsShared(): AbstractControl {
    return this.form.get('IsShared');
  }

  get IsSharedWithAllUsers(): AbstractControl {
    return this.form.get('IsSharedWithAllUsers');
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly dataService: PlaylistsService,
    private readonly playlistItemsService: PlaylistItemsService,
    private readonly tagService: TagService,
    private readonly screenStateService: ScreensStateService,
    private readonly uiService: UIService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog
  ) {
    this.initSubscriptions();
    this.subscriptions.add(
      this.screenStateService.locationChanged.subscribe((location: string) => this.locationChanged.emit(location))
    );
  }

  ngOnInit(): void {
    this.initForm();

    this.authService
      .getUsersFilteredByEmail('')
      .subscribe((data) => {
        this.allUsers = data;
        if (this.allUsers.length || !this.isOwner) {
          this.form.get('IsShared').enable();
        } else {
          this.form.get('IsShared').setValue(false);
        }
      });

    if (this.editItem.Id) {
      this.getUsersWithAccessToItem();
      if (this.editItemType === LibraryEditItemTypeEnum.EditPlaylist) {
        this.isOwner = this.editItem.UserId === this.currentUserId;
      } else if (this.editItemType === LibraryEditItemTypeEnum.EditTag) {
        this.isOwner = this.editItem.CreatorUserId === this.currentUserId;
      }
    }
    this.playlistTitleElement.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onBlur(state: boolean) {
    if (this.Title.value != '' && state) {
      this.isTitleFocused = true;
    } else {
      this.isTitleFocused = false;
    }
    this.dataService.titleState.next(this.isTitleFocused);
  }

  delete(user: PersonalDataViewModel): void {
    _.remove(this.selectedUsersShared, user);
    this.form.markAsDirty();
    this.updateSelectedUsersIds();
  }

  onSelect(user: PersonalDataViewModel) {
    this.selectedUsersShared.push(user);
    this.autocompleteUserInput = '';
    this.filteredUsers = [];
    this.form.markAsDirty();
    this.updateSelectedUsersIds();
  }

  autocompleteChanged(filterValue: string): void {
    this.filteredUsers = [];
    this.subscriptions.add(
      this.authService
        .getUsersFilteredByEmail(filterValue)
        .subscribe((data) => {
          this.filteredUsers = _.differenceBy(
            data,
            this.selectedUsersShared,
            'Id'
          );
        })
    );
  }

  // Clean the input field when a value was selected
  matAutocompleteDisplayFn(): null {
    return null;
  }

  drop(event: { previousIndex: number; currentIndex: number }): void {
    moveItemInArray(
      this.editItem.PlaylistItems,
      event.previousIndex,
      event.currentIndex
    );
  }

  isEditTypeEqualToPlaylist(): boolean {
    return this.editItemType === this.libraryEditItemTypes.EditPlaylist;
  }

  onIsAndroidChanged(isAndroid: boolean): void {
    this.form.markAsDirty();

    if (isAndroid && this.editItem.PlaylistItems.length) {
      const dialogRef = this.dialog.open(ModalConfirmChangePlaylistTypeComponent, {
        width: AppConstants.CONFIRM_PLAYLIST_TYPE_CHANGE_MODAL_WINDOW_WIDTH
      });
      dialogRef.componentInstance.playlist = this.editItem;

      dialogRef.afterClosed()
        .pipe(take(1))
        .subscribe(
          (isAndroid: boolean) => {
            this.playlist.IsAndroid = isAndroid;
            if (isAndroid) {
              this.form.patchValue({
                Transition: TransitionEffect.Cut,
                FitToVideoSize: false
              });
              this.playlist.PlaylistItems = this.playlistItemsService.transformItems(this.playlist);
            }
          }
        );
    } else {
      this.playlist.IsAndroid = isAndroid;
      this.playlist.PlaylistItems = this.playlistItemsService.transformItems(this.playlist);
    }
  }

  onShareWithAllChanged(isShareWithAll: boolean): void {
    this.editItem.IsSharedWithAllUsers = isShareWithAll;
    if (!isShareWithAll) {
      this.selectedUsersShared = [];
      this.UsersWhoHaveAccessIds = [];
    }
  }

  originalOrder(): number {
    return 0;
  }

  private initSubscriptions(): void {
    this.subscriptions.add(
      this.screenStateService.locationsChanged.subscribe(
        (data) => (this.locations = data)
      )
    );

    this.subscriptions.add(
      this.uiService.bottomPanelVisibleSubject.subscribe((data) => {
        this.isBottomPanelVisible = data;
      })
    );

    if (this.authService.userId) {
      this.currentUserId = this.authService.userId;
    } else {
      this.subscriptions.add(
        this.authService.getCaller().subscribe((data) => {
          this.currentUserId = data.Id;
        })
      );
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      Title: [
        this.editItem.Title,
        [
          Validators.required,
          Validators.maxLength(50),
          this.nameValidator.bind(this),
        ],
      ],
      Description: [this.editItem.Description, [Validators.maxLength(200)]],
      Transition: this.editItem.Transition,
      IsShared: [{value: this.editItem.IsShared, disabled: true}],
      IsRepeated: [this.editItem.IsRepeated],
      IsSharedWithAllUsers: [this.editItem.IsSharedWithAllUsers],
      FitToVideoSize: this.editItem.FitToVideoSize
    });
  }

  private updateSelectedUsersIds(): void {
    this.UsersWhoHaveAccessIds = this.selectedUsersShared.map(
      (selectedUser) => selectedUser.Id
    );
    this.checkIfAllUsersSelected();
  }

  private checkIfAllUsersSelected(): void {
    if (this.allUsers.length > this.UsersWhoHaveAccessIds.length) {
      this.form.get('IsSharedWithAllUsers').setValue(false);
    }
  }

  private getUsersWithAccessToItem(): void {
    if (this.editItemType === LibraryEditItemTypeEnum.EditTag) {
      this.subscriptions.add(
        this.tagService
          .getOneTag(this.editItem.Id)
          .subscribe((tag: TagViewModel) => {
            if (tag.SharedUsers) {
              this.selectedUsersShared = _.filter(
                tag.SharedUsers,
                (x: UpdateProfileViewModel) => x.Id !== this.editItem.UserId
              );
              this.UsersWhoHaveAccessIds = this.selectedUsersShared.map(
                (selectedUser) => selectedUser.Id
              );
            }
            this.editItem.IsSharedWithAllUsers = tag.IsSharedWithAllUsers;
            this.form.get('IsSharedWithAllUsers').setValue(tag.IsSharedWithAllUsers);
            this.dateCreated = new Date(tag.CreationDateTime);
          })
      );
    } else if (this.editItemType === LibraryEditItemTypeEnum.EditPlaylist) {
      this.subscriptions.add(
        this.dataService
          .getOnePlaylist(this.editItem.Id)
          .subscribe((playlist) => {
            if (playlist.UsersWhoHaveAccess) {
              this.selectedUsersShared = _.filter(
                playlist.UsersWhoHaveAccess,
                (x) => x.Id !== this.editItem.UserId
              );
              this.UsersWhoHaveAccessIds = this.selectedUsersShared.map(
                (selectedUser) => selectedUser.Id
              );
            }
            this.editItem.IsSharedWithAllUsers = playlist.IsSharedWithAllUsers;
            this.form.get('IsSharedWithAllUsers').setValue(playlist.IsSharedWithAllUsers);
            this.dateCreated = new Date(playlist.DateCreated);
          })
      );
    }
  }

  private nameValidator(control: AbstractControl): ValidationErrors {
    if (
      control.value &&
      this.existedTitles.some(
        (name) => name.toLowerCase() === control.value.toLowerCase()
      )
    ) {
      return { nameExists: true };
    }

    return null;
  }
}
